import React, { useEffect, useState, useRef } from 'react';
import { motion, useReducedMotion, useInView } from 'framer-motion';
import { Shield, Cpu, HardDrive } from 'lucide-react';
import { useCopy } from './copy/copyMode';
// Helper to type out command text character by character
function TypewriterText({
  text
}: {
  text: string;
}) {
  const [chars, setChars] = useState(0);
  const reducedMotion = useReducedMotion();
  useEffect(() => {
    if (reducedMotion) {
      setChars(text.length);
      return;
    }
    setChars(0);
    const interval = setInterval(() => {
      setChars(c => {
        if (c >= text.length) {
          clearInterval(interval);
          return c;
        }
        return c + 1;
      });
    }, 30); // 30ms per character
    return () => clearInterval(interval);
  }, [text, reducedMotion]);
  return <span data-id="element-906">{text.slice(0, chars)}</span>;
}
// Terminal lines to animate
const TERMINAL_LINES = [{
  text: 'giga@agent-7f32:~/workspace$ ',
  type: 'prompt',
  delay: 0
}, {
  text: 'giga browser export --site billing.portal',
  type: 'command',
  delay: 400
}, {
  text: 'Restored authenticated browser session',
  type: 'output',
  delay: 2000
}, {
  text: 'Downloaded 184 invoices to ./downloads/invoices/',
  type: 'output',
  delay: 2500
}, {
  text: '',
  type: 'empty',
  delay: 2600
}, {
  text: 'giga@agent-7f32:~/workspace$ ',
  type: 'prompt',
  delay: 2900
}, {
  text: 'python reconcile.py',
  type: 'command',
  delay: 3300
}, {
  text: '✓ Reconciled totals · report.pdf ready',
  type: 'success',
  delay: 4300
}, {
  text: '',
  type: 'empty',
  delay: 4400
}, {
  text: 'giga@agent-7f32:~/workspace$ ',
  type: 'prompt',
  delay: 4700
}];
export function ComputerUse() {
  const copy = useCopy();
  const reducedMotion = useReducedMotion();
  const terminalRef = useRef<HTMLDivElement>(null);
  // Only kick off the typing sequence once the terminal scrolls into view
  const inView = useInView(terminalRef, {
    once: true,
    margin: '-120px'
  });
  const [visibleLines, setVisibleLines] = useState<number>(reducedMotion ? TERMINAL_LINES.length : 0);
  useEffect(() => {
    if (reducedMotion) {
      setVisibleLines(TERMINAL_LINES.length);
      return;
    }
    if (!inView) return;
    setVisibleLines(0);
    let timeouts: NodeJS.Timeout[] = [];
    TERMINAL_LINES.forEach((line, index) => {
      const timeout = setTimeout(() => {
        setVisibleLines(index + 1);
      }, line.delay);
      timeouts.push(timeout);
    });
    return () => timeouts.forEach(clearTimeout);
  }, [reducedMotion, inView]);
  return <>
      {/* DESKTOP LAYOUT (Old Version) */}
      <section className="hidden md:block relative w-full bg-[#FAF8F4] py-32 overflow-hidden" data-id="element-907">
        <div className="absolute inset-0 z-0 pointer-events-none mix-blend-multiply opacity-90" data-id="element-908">
          <video src="/hf_20260616_083352_929e6c52-e5b2-425a-882f-180098bd55b1.mp4" autoPlay loop muted playsInline className="w-full h-full object-cover object-[20%_center] grayscale" data-id="element-909" />
        </div>

        <div className="relative z-10 max-w-[1280px] mx-auto px-10 flex justify-end" data-id="element-910">
          <div className="w-[55%] xl:w-[50%] flex flex-col gap-16 relative pt-12 pr-12" data-id="element-911">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.6,
            delay: 0.1
          }} data-id="element-912">
              <span className="block text-xs font-semibold tracking-[5px] uppercase text-neutral-400 mb-6" data-id="element-913">
                Under the hood
              </span>
              <h2 className="text-3xl md:text-5xl font-title font-medium tracking-tight leading-[1.1] text-[#2B211A] mb-6" data-id="element-914">
                {copy.computerUse.headline}
              </h2>
              <p className="text-xl text-neutral-500 leading-relaxed max-w-xl" data-id="element-915">
                Every task runs on Giga's own secure machine in the cloud — it
                writes code, crunches files, and opens a browser when work lives
                behind a login.
              </p>
            </motion.div>

            <motion.div ref={terminalRef} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.6,
            delay: 0.25
          }} className="relative max-w-2xl z-10 w-full" data-id="element-916">
              <div className="w-full rounded-2xl bg-[#1C1510] border border-white/10 shadow-2xl overflow-hidden flex flex-col font-mono text-sm" data-id="element-917">
                {/* Window Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06] bg-[#241B14]" data-id="element-918">
                  <div className="flex items-center gap-2 w-24" data-id="element-919">
                    <div className="w-3 h-3 rounded-full bg-[#FF5F56]" data-id="element-920" />
                    <div className="w-3 h-3 rounded-full bg-[#FFBD2E]" data-id="element-921" />
                    <div className="w-3 h-3 rounded-full bg-[#27C93F]" data-id="element-922" />
                  </div>
                  <div className="flex items-center gap-2 text-[#9C8E82] text-xs font-medium truncate" data-id="element-923">
                    <Cpu size={12} className="opacity-70 shrink-0 w-3.5 h-3.5" data-id="element-924" />
                    <span className="truncate" data-id="element-925">
                      agent-7f32 · isolated sandbox
                    </span>
                  </div>
                  <div className="w-24 flex justify-end" data-id="element-926">
                    <div className="flex items-center gap-1.5 px-1.5 py-0.5 rounded border border-[#F97316]/30 bg-[#F97316]/10 text-[#F97316] text-[10px] font-bold tracking-wider uppercase" data-id="element-927">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#F97316] animate-pulse" data-id="element-928" />
                      LIVE
                    </div>
                  </div>
                </div>

                {/* Tabs */}
                <div className="flex items-center px-4 border-b border-white/[0.06] bg-[#241B14] text-xs" data-id="element-929">
                  <div className="px-4 py-2 border-b-2 border-[#F97316] text-white font-medium" data-id="element-930">
                    Terminal
                  </div>
                  <div className="px-4 py-2 text-[#9C8E82] font-medium" data-id="element-931">
                    Files
                  </div>
                  <div className="px-4 py-2 text-[#9C8E82] font-medium" data-id="element-932">
                    Browser
                  </div>
                </div>

                {/* Terminal Body */}
                <div className="p-6 min-h-[240px] bg-[#1C1510] text-[#E7E0D7] overflow-hidden" data-id="element-933">
                  <div className="flex flex-col gap-1.5 w-full whitespace-pre-wrap" data-id="element-934">
                    {TERMINAL_LINES.slice(0, visibleLines).map((line, i) => {
                    if (line.type === 'empty') return <div key={i} className="h-4" data-id="element-935" />;
                    const isLastVisible = i === visibleLines - 1;
                    const isPrompt = line.type === 'prompt';
                    const isCommand = line.type === 'command';
                    const isSuccess = line.type === 'success';
                    if (isPrompt) {
                      const nextLine = TERMINAL_LINES[i + 1];
                      const hasCommand = nextLine && nextLine.type === 'command' && visibleLines > i + 1;
                      const isCommandLastVisible = hasCommand && visibleLines === i + 2;
                      return <div key={i} className="flex items-center gap-2" data-id="element-936">
                            <span className="text-[#F97316]" data-id="element-937">{line.text}</span>
                            {hasCommand ? <div className="flex items-center gap-0.5" data-id="element-938">
                                <span className="text-[#FAF8F4]" data-id="element-939">
                                  <TypewriterText text={nextLine.text} data-id="element-940" />
                                </span>
                                {isCommandLastVisible && <span className="w-2 h-4 bg-[#F97316] animate-pulse" data-id="element-941" />}
                              </div> : isLastVisible && <span className="w-2 h-4 bg-[#F97316] animate-pulse" data-id="element-942" />}
                          </div>;
                    }
                    if (isCommand) return null;
                    if (isSuccess) return <div key={i} className="text-[#F97316] font-semibold" data-id="element-943">
                            {line.text}
                          </div>;
                    return <div key={i} className="text-[#9C8E82]" data-id="element-944">
                          {line.text}
                        </div>;
                  })}
                  </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-4 py-2.5 border-t border-white/[0.06] bg-[#241B14] text-xs text-[#9C8E82]" data-id="element-945">
                  <div className="flex items-center gap-4" data-id="element-946">
                    <div className="flex items-center gap-1.5" data-id="element-947">
                      <Cpu size={12} className="w-3.5 h-3.5" data-id="element-948" />2 vCPU
                    </div>
                    <div className="flex items-center gap-1.5" data-id="element-949">
                      <HardDrive size={12} className="w-3.5 h-3.5" data-id="element-950" />
                      14.8 GB free
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 text-[#E7E0D7]" data-id="element-951">
                    <Shield size={12} className="w-3.5 h-3.5" data-id="element-952" />
                    isolated
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* MOBILE LAYOUT (New Version) */}
      <section className="md:hidden relative w-full bg-[#FAF8F4] py-12 overflow-hidden" data-id="element-953">
        {/* Subtle warm dot-grid backdrop, matching the desktop surface. */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-0 opacity-60" style={{
        backgroundImage: 'radial-gradient(rgba(43,33,26,0.10) 1px, transparent 1px)',
        backgroundSize: '22px 22px'
      }} data-id="element-954" />
        <div className="relative z-10 max-w-3xl mx-auto px-6 flex flex-col items-start" data-id="element-955">
          <motion.div initial={reducedMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.6
        }} className="w-full mb-4" data-id="element-956">
            <span className="block text-xs font-semibold tracking-[5px] uppercase text-neutral-400 mb-5" data-id="element-957">
              Under the hood
            </span>
            <h2 className="text-3xl md:text-5xl font-title font-medium tracking-tight leading-[1.1] text-[#2B211A]" data-id="element-958">
              {copy.computerUse.headline}
            </h2>
          </motion.div>

          <motion.div initial={reducedMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.6,
          delay: reducedMotion ? 0 : 0.2
        }} className="w-full mb-6" data-id="element-959">
            {/* Composed scene (replaces the video): the isolated-sandbox
             terminal sits to the upper-right, and the grayscale "computer-head
             person with laptop" overlaps it in front from the lower-left —
             matching the reference exactly. Both are static images. */}
            <div className="relative w-full aspect-[3/3.4]" data-id="element-960">
              {/* Terminal window — top-right, behind the person. */}
              <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/0e427b37-c883-4901-a20e-16572a4d8a37/Container_(4).png" alt="Giga running tasks in an isolated cloud sandbox terminal" className="absolute top-[2%] right-0 w-[74%] h-auto z-10 drop-shadow-2xl" data-id="element-961" />
              {/* Computer-head person with laptop — lower-left, in front.
               Enlarged to match the Figma reference where the figure is the
               dominant element of the composition. */}
              <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/0e427b37-c883-4901-a20e-16572a4d8a37/image_32.png" alt="Giga, illustrated as a computer-headed figure working on a laptop" className="absolute bottom-0 -left-[4%] w-[88%] h-auto z-20" data-id="element-962" />
            </div>
          </motion.div>

          <motion.div initial={reducedMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.6,
          delay: reducedMotion ? 0 : 0.4
        }} className="w-full" data-id="element-963">
            <p className="text-base text-neutral-500 leading-relaxed font-medium" data-id="element-964">
              Every task runs on Giga's own secure machine in the cloud — it
              writes code, crunches files, and opens a browser when work lives
              behind a login.
            </p>
          </motion.div>
        </div>
      </section>
    </>;
}